import React, {
  CSSProperties,
  FocusEventHandler,
  useEffect,
  useState,
} from 'react';
import { forwardRef } from 'react';
import {
  NumericFormat,
  NumericFormatProps,
  OnValueChange,
} from 'react-number-format';
import styled from 'styled-components';

interface NumberInputProps extends Omit<NumericFormatProps, 'onChange'> {
  emptyValue?: number | string;
  errMsg?: string;
  actionButton?: boolean;
  onChange?: (value: number | null) => void;
  styleSlot?: {
    errMsg?: CSSProperties;
    inputContainer?: CSSProperties;
  };
}

const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
  (props, ref) => {
    const {
      value,
      style,
      min = 0,
      max = Infinity,
      emptyValue,
      actionButton = true,
      onChange,
      onBlur,
      errMsg,
      styleSlot,
      ...restProps
    } = props;
    const [inputValue, setInputValue] =
      useState<NumericFormatProps['value']>(value);

    const disabledMinus =
      inputValue === undefined || inputValue === null || +inputValue <= +min;

    const disabledPlus =
      inputValue !== undefined && inputValue !== null && +inputValue >= +max;

    const inputEmptyValue = emptyValue ?? min;

    const handleChangeValue: OnValueChange = values => {
      const floatValue = values.floatValue ?? null;
      setInputValue(floatValue);
      onChange?.(floatValue);
    };

    const handleIncrement = () => {
      let nextValue = !!inputValue ? +inputValue : 0;
      setInputValue(nextValue + 1);
    };

    const handleDecrement = () => {
      let nextValue = !!inputValue ? +inputValue : 0;
      setInputValue(nextValue - 1);
    };

    const handleBlur: FocusEventHandler<HTMLInputElement> = e => {
      if (inputValue === null) {
        setInputValue(inputEmptyValue);
      }
      onBlur?.(e);
    };

    useEffect(() => {
      setInputValue(value);
    }, [value]);

    return (
      <Container>
        <InputContainer
          className="d-flex"
          style={{
            borderColor: !!errMsg ? '#e92127' : '#e7e7e7',
            ...styleSlot?.inputContainer,
          }}
        >
          {actionButton && (
            <Button onClick={handleDecrement} disabled={disabledMinus}>
              <i className={`fa-solid fa-minus`} />
            </Button>
          )}
          <NumericFormat
            value={inputValue}
            getInputRef={ref}
            allowNegative={false}
            decimalScale={0}
            fixedDecimalScale
            thousandSeparator={false}
            decimalSeparator=","
            inputMode="numeric"
            onValueChange={handleChangeValue}
            onBlur={handleBlur}
            isAllowed={values => {
              const { floatValue } = values;
              return !floatValue || floatValue <= +max;
            }}
            style={{
              padding: actionButton ? '0px' : '0px 10px',
              ...inputStyles,
              ...style,
            }}
            {...restProps}
          />
          {actionButton && (
            <Button onClick={handleIncrement} disabled={disabledPlus}>
              <i className={`fa-solid fa-plus`} />
            </Button>
          )}
        </InputContainer>
        {errMsg && <ErrMsg style={styleSlot?.errMsg}>{errMsg}</ErrMsg>}
      </Container>
    );
  },
);

export default NumberInput;

const Container = styled.div`
  position: relative;
`;

const InputContainer = styled.div`
  background: #ffffff;
  border: 1px solid #e7e7e7;
  border-radius: 6px;
  height: 35px;
  width: 106px;
`;

const Button = styled.button`
  border: none;
  outline: none;
  background: transparent;
  color: #e92127;
  &:disabled {
    color: #a5a4a4;
  }
`;

const inputStyles: CSSProperties = {
  border: 'none',
  outline: 'none',
  textAlign: 'center',
  borderRadius: 6,
  width: '100%',
};

const ErrMsg = styled.span`
  color: #e92127;
  display: inline-block;
`;
